/**
 * Bypass-aware re-exports of Convex's React surface.
 *
 * Under NEXT_PUBLIC_DEV_AUTH_BYPASS=true the app does not have a real
 * Clerk session, so any Convex function gated by `requireIdentity()`
 * would 401 and throw inside `useQuery`. We can't reach the Convex
 * backend with a valid token in that mode, so we short-circuit:
 *  - `<Authenticated>` renders its children (we pretend the bypass
 *    user is signed in for UI purposes)
 *  - `<Unauthenticated>` / `<AuthLoading>` render nothing
 *  - `useQuery` returns `undefined` (the universal "loading" sentinel)
 *  - `useMutation` returns a no-op
 *  - `usePaginatedQuery` returns an empty result
 *
 * In normal mode the exports forward to `convex/react` verbatim.
 *
 * Components that depend on Convex data should import from here
 * instead of `convex/react` when they may render under bypass.
 */

import type { ReactNode } from 'react';
import * as ConvexReact from 'convex/react';
import { isDevAuthBypassEnabled } from '@/lib/dev/auth-bypass-flag';

export function Authenticated({ children }: { children: ReactNode }) {
  if (isDevAuthBypassEnabled) return <>{children}</>;
  return <ConvexReact.Authenticated>{children}</ConvexReact.Authenticated>;
}

export function Unauthenticated({ children }: { children: ReactNode }) {
  if (isDevAuthBypassEnabled) return null;
  return <ConvexReact.Unauthenticated>{children}</ConvexReact.Unauthenticated>;
}

export function AuthLoading({ children }: { children: ReactNode }) {
  if (isDevAuthBypassEnabled) return null;
  return <ConvexReact.AuthLoading>{children}</ConvexReact.AuthLoading>;
}

// We re-export Convex's hooks with the EXACT same identity in normal mode
// so TypeScript carries through the overload signatures. Under bypass we
// substitute simple shims that don't preserve overloads — that's fine
// because they only run at runtime, and bypass is dev-only.
//
// The cast through `unknown` keeps TS happy without us reimplementing
// the entire (very large) overload set.

const bypassQuery = (() => undefined) as unknown as typeof ConvexReact.useQuery;
const bypassMutation = (() => () =>
  Promise.resolve(undefined)) as unknown as typeof ConvexReact.useMutation;
const bypassAction = (() => () =>
  Promise.resolve(undefined)) as unknown as typeof ConvexReact.useAction;
const bypassPaginated = (() => ({
  results: [] as never[],
  status: 'Exhausted' as const,
  loadMore: () => {},
  isLoading: false,
})) as unknown as typeof ConvexReact.usePaginatedQuery;

export const useQuery: typeof ConvexReact.useQuery = isDevAuthBypassEnabled
  ? bypassQuery
  : ConvexReact.useQuery;
export const useMutation: typeof ConvexReact.useMutation = isDevAuthBypassEnabled
  ? bypassMutation
  : ConvexReact.useMutation;
export const useAction: typeof ConvexReact.useAction = isDevAuthBypassEnabled
  ? bypassAction
  : ConvexReact.useAction;
export const usePaginatedQuery: typeof ConvexReact.usePaginatedQuery = isDevAuthBypassEnabled
  ? bypassPaginated
  : ConvexReact.usePaginatedQuery;
